'use client';
import { useMemo, useState } from 'react';
import { AchievementCard } from './AchievementCard';
import { AchievementDetailSheet } from './AchievementDetailSheet';
import { useClaimSheetStore } from '@/stores/claimSheetStore';
import { useUiMotionEnabled } from '@/hooks/useMotion';
import { cn } from '@/utils/cn';
import type { MergedAchievement } from '../hooks/useMyAchievements';

interface Props {
  items: MergedAchievement[];
  className?: string;
  viewerIsOwner?: boolean;
}

const SETTLE_STAGGER_MS = 60;
const SETTLE_MAX_TILES = 10;

export function AchievementGrid({ items, className, viewerIsOwner = true }: Props) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const motion = useUiMotionEnabled();
  const justClaimed = useClaimSheetStore((s) => s.justClaimed);

  const settle = useMemo(() => {
    const plan = new Map<string, { delayMs: number; pop: boolean }>();
    if (!motion || !viewerIsOwner || justClaimed.size === 0) return plan;
    let n = 0;
    for (const a of items) {
      if (!justClaimed.has(a.key)) continue;
      const pop = n < SETTLE_MAX_TILES;
      plan.set(a.key, { delayMs: Math.min(n, SETTLE_MAX_TILES - 1) * SETTLE_STAGGER_MS, pop });
      n += 1;
    }
    return plan;
  }, [items, justClaimed, motion, viewerIsOwner]);

  return (
    <>
      <div className={cn('grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 p-4', className)}>
        {items.map((a) => (
          <AchievementCard
            key={a.key}
            achievement={a}
            onOpen={setOpenKey}
            motion={motion}
            settle={settle.get(a.key)}
            viewerIsOwner={viewerIsOwner}
          />
        ))}
      </div>
      <AchievementDetailSheet
        achievementKey={openKey}
        onClose={() => setOpenKey(null)}
        viewerIsOwner={viewerIsOwner}
        ownerAchievement={openKey ? (items.find((a) => a.key === openKey) ?? null) : null}
      />
    </>
  );
}
